import { MIDDLEA, SEMITONE } from '~/consts';
import pitchToFrequency from '~/modules/utils/pitch-to-frequency';

// Unrounded, so a caller can tell a note held dead centre from one drifting towards its neighbour.
// Rounding it is the note name, which is all the scoring path ever wanted.
export const precisePitchFromFrequency = (freq: number) => 12 * (Math.log(freq / MIDDLEA) / Math.log(2)) + SEMITONE;

export const pitchFromFrequency = (freq: number) => Math.round(precisePitchFromFrequency(freq));

const getDistanceInCents = (noteFreq: number, freq: number) =>
  Math.floor((1200 * Math.log(freq / noteFreq)) / Math.log(2));

const getCentDistance = (targetNote: number, freq: number, tolerance: number) => {
  const noteFreq = pitchToFrequency(targetNote);
  const cents = getDistanceInCents(noteFreq, freq);
  // To handle cases for cents like -1150 (=50) or 1150 (=-50)
  const distance = Math.sign(cents) * ((((Math.abs(cents) % 1200) + 600) % 1200) - 600);

  return distance / (tolerance * 100 + 50);
};

export const calcDistanceBetweenPitches = (note: number, targetNote: number, tolerance: number) => {
  // +6 / - 6 are here to avoid distances like -8 in favor of +4
  // (so smallest between "too high" or "too low" pitch to hit the note in whatever octave)
  const noteDistance = (((note % 12) - (targetNote % 12) + 18) % 12) - 6;

  return Math.abs(noteDistance) <= tolerance ? 0 : noteDistance;
};

// Tolerance is passed in rather than read off the `GameState` singleton. It is the one number that
// decides what every figure downstream means, and reaching for it here made a pure calculation
// depend on global state that no caller could see and no test could vary.
export const calcDistance = (frequency: number, targetNote: number, tolerance: number) => {
  const note = pitchFromFrequency(frequency);
  const distance = calcDistanceBetweenPitches(note, targetNote, tolerance);

  return {
    distance,
    // Only measured inside the tolerance; outside it there is no fine offset to report.
    preciseDistance: distance === 0 ? getCentDistance(targetNote, frequency, tolerance) : null,
  };
};
